import React, { useState, useRef, useMemo, useEffect } from "react";
import { Clock, User, CheckCircle, AlertCircle, FileText } from "lucide-react";

import { getExaminationTemplateDetails } from "../../../../services/Users-services.js";
import {
  PAPER_SIZES,
  setInitialExamData,
  RichContentRenderer,
} from "../../../Examinations/TepmpateEditor/utility.js";



const ExamPage = ({ examId = "1395517644239859712" }) => {
  const canvasRef = useRef(null);

  // State management
  const [exam, setExam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [responses, setResponses] = useState({});
  const [submittedQuestions, setSubmittedQuestions] = useState({});
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showSubmitConfirmation, setShowSubmitConfirmation] = useState(false);

  // Fetch exam data
  useEffect(() => {
    const fetchExamData = async () => {
      try {
        setLoading(true);
        const response = await getExaminationTemplateDetails(examId);

        if (response.status === "success") {
          const examData = setInitialExamData(response.data);

          // Filter only objective-type questions
          const filteredExam = {
            ...examData,
            pages:
              examData.pages
                ?.map((page) => ({
                  ...page,
                  sections:
                    page.sections
                      ?.map((section) => ({
                        ...section,
                        questions:
                          section.questions?.filter(
                            (q) => q.type === "with-options"
                          ) || [],
                      }))
                      .filter((section) => section.questions.length > 0) || [],
                }))
                .filter((page) => page.sections.length > 0) || [],
          };

          setExam(filteredExam);

          // Initialize timer if duration is provided
          if (
            examData.metadata?.duration &&
            examData.metadata.duration !== "00:00:00"
          ) {
            const [hours, minutes, seconds] = examData.metadata.duration
              .split(":")
              .map(Number);
            const totalSeconds = hours * 3600 + minutes * 60 + seconds;
            setTimeRemaining(totalSeconds);
          }

          setError(null);
        } else {
          setError("Failed to load exam data");
        }
      } catch (err) {
        console.error("Error fetching exam data:", err);
        setError("Failed to load exam. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    if (examId) {
      fetchExamData();
    }
  }, [examId]);

  // Timer effect
  useEffect(() => {
    if (timeRemaining === null || timeRemaining <= 0 || isSubmitted) {
      return;
    }

    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          handleAutoSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeRemaining, isSubmitted]);

  // Handle individual question submission
  const handleQuestionSubmit = (questionId) => {
    if (isSubmitted) return;

    const response = responses[questionId];
    if (response === undefined) {
      alert("Please select an answer before submitting this question.");
      return;
    }

    // Mark question as submitted
    setSubmittedQuestions((prev) => ({
      ...prev,
      [questionId]: true,
    }));

    // Show confirmation
    const questionNumber = getQuestionNumber(questionId);
    alert(`Question ${questionNumber} submitted successfully!`);
  };

  // Get question number for display
  const getQuestionNumber = (questionId) => {
    let questionNumber = 1;
    if (!exam?.pages) return questionNumber;

    for (const page of exam.pages) {
      for (const section of page.sections) {
        for (const question of section.questions) {
          if (question.id === questionId) {
            return questionNumber;
          }
          questionNumber++;
        }
      }
    }
    return questionNumber;
  };

  // Handle option selection
  const handleOptionSelect = (questionId, optionIndex) => {
    if (isSubmitted || submittedQuestions[questionId]) return;

    setResponses((prev) => ({
      ...prev,
      [questionId]: optionIndex,
    }));
  };

  // Handle exam submission
  const handleSubmit = () => {
    setShowSubmitConfirmation(true);
  };

  const confirmSubmit = async () => {
    try {
      setIsSubmitted(true);
      setShowSubmitConfirmation(false);

      // Here you would typically send responses to the backend
      console.log("Exam responses:", responses);

      // Calculate basic statistics
      const totalQuestions = getTotalQuestions();
      const answeredQuestions = Object.keys(responses).length;

      alert(
        `Exam submitted successfully!\nAnswered: ${answeredQuestions}/${totalQuestions} questions`
      );

      // You can add custom navigation logic here if needed
      // Example: window.location.href = `/exam-results/${examId}`;
    } catch (error) {
      console.error("Error submitting exam:", error);
      alert("Failed to submit exam. Please try again.");
      setIsSubmitted(false);
    }
  };

  const handleAutoSubmit = () => {
    if (!isSubmitted) {
      setIsSubmitted(true);
      alert("Time's up! Your exam has been automatically submitted.");
      confirmSubmit();
    }
  };

  // Utility functions
  const getTotalQuestions = () => {
    if (!exam?.pages) return 0;
    return exam.pages.reduce(
      (total, page) =>
        total +
        page.sections.reduce(
          (sectionTotal, section) => sectionTotal + section.questions.length,
          0
        ),
      0
    );
  };

  const getAnsweredQuestions = () => {
    return Object.keys(responses).length;
  };

  const formatTime = (seconds) => {
    if (seconds === null) return "";
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // Organize content into pages (same logic as canvas.js)
  const organizedPages = useMemo(() => {
    if (!exam?.pages) return [];

    const pageHeight = PAPER_SIZES.A4.height - 160;
    const questionBaseHeight = 75;
    const optionHeight = 25;
    const sectionHeaderHeight = 50;
    const examHeaderHeight = 200;

    let organizedPages = [];
    let currentPageHeight = 0;
    let currentPage = null;
    let pageIndex = 0;

    const createNewPage = () => {
      const newPage = {
        id: `page-${Date.now()}-${pageIndex}`,
        sections: [],
      };

      currentPageHeight = pageIndex === 0 ? examHeaderHeight + 30 : 50;
      pageIndex++;
      return newPage;
    };

    const calculateQuestionHeight = (question) => {
      let height = questionBaseHeight;
      if (question.options && question.options.length > 0) {
        height += question.options.length * optionHeight;
      }
      return height;
    };

    // Process all sections and questions
    exam.pages.forEach((page) => {
      page.sections.forEach((section) => {
        const sectionHeight =
          sectionHeaderHeight +
          (section.hasInstructions ? 35 : 0) +
          section.questions.reduce(
            (total, q) => total + calculateQuestionHeight(q),
            0
          );

        if (!currentPage || currentPageHeight + sectionHeight > pageHeight) {
          if (currentPage) {
            organizedPages.push(currentPage);
          }
          currentPage = createNewPage();
        }

        currentPage.sections.push({
          ...section,
          questions: section.questions.filter((q) => q.type === "with-options"),
        });

        currentPageHeight += sectionHeight;
      });
    });

    if (currentPage && currentPage.sections.length > 0) {
      organizedPages.push(currentPage);
    }

    return organizedPages;
  }, [exam]);

  // Loading state
  if (loading) {
    return (
      <div className="exam-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading exam...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="exam-page">
        <div className="error-container">
          <AlertCircle className="error-icon" />
          <h2>Error Loading Exam</h2>
          <p>{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="retry-btn"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // No exam data
  if (!exam) {
    return (
      <div className="exam-page">
        <div className="error-container">
          <FileText className="error-icon" />
          <h2>Exam Not Found</h2>
          <p>The requested exam could not be found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="exam-page">
      {/* Exam Header with Timer and Progress */}
      <div className="exam-header">
        <div className="exam-header-content">
          <div className="exam-info">
            <h1 className="exam-title">{exam.metadata?.name || "Exam"}</h1>
            <p className="exam-subject">{exam.metadata?.subject}</p>
          </div>

          <div className="exam-stats">
            {timeRemaining !== null && (
              <div
                className={`timer ${
                  timeRemaining < 300 ? "timer-warning" : ""
                }`}
              >
                <Clock size={20} />
                <span>{formatTime(timeRemaining)}</span>
              </div>
            )}

            <div className="progress-info">
              <User size={20} />
              <span>
                {getAnsweredQuestions()}/{getTotalQuestions()}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Exam Content */}
      <div className="exam-content">
        <div className="paper-container" ref={canvasRef}>
          {organizedPages.map((page, pageIndex) => (
            <div key={pageIndex} className="exam-page-content">
              {/* Exam Header (only on first page) */}
              {pageIndex === 0 && (
                <div className="exam-details-header">
                  <div className="exam-header-main">
                    <h1 className="exam-name">
                      {exam.metadata?.name || "Examination"}
                    </h1>
                    <h2 className="exam-subject-title">
                      {exam.metadata?.subject || "Subject"}
                    </h2>
                  </div>

                  <div className="exam-metadata">
                    <div className="exam-duration">
                      Time:{" "}
                      {exam.metadata?.duration
                        ? (() => {
                            const [hours, minutes] =
                              exam.metadata.duration.split(":");
                            return `${parseInt(hours)} Hours ${parseInt(
                              minutes
                            )} Minutes`;
                          })()
                        : "Not specified"}
                    </div>
                    <div className="exam-marks">
                      Maximum Marks: {exam.metadata?.totalMarks || exam.score}
                    </div>
                  </div>

                  {exam.metadata?.instructions && (
                    <div className="exam-instructions">
                      <strong>Instructions:</strong>
                      <RichContentRenderer
                        content={
                          exam.metadata.instructionsRichContent ||
                          exam.metadata.instructions
                        }
                        isNonEditable={true}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Sections and Questions */}
              {page.sections.map((section, sectionIndex) => (
                <div key={section.id} className="section">
                  <div className="section-header">
                    <h3 className="section-title">
                      <RichContentRenderer
                        content={section.titleRichContent || section.title}
                        isNonEditable={true}
                      />
                    </h3>
                  </div>

                  {section.hasInstructions && section.instructions && (
                    <div className="section-instructions">
                      <div className="instructions-label">Instructions:</div>
                      <div className="instructions-content">
                        <RichContentRenderer
                          content={
                            section.instructionsRichContent ||
                            section.instructions
                          }
                          isNonEditable={true}
                        />
                      </div>
                    </div>
                  )}

                  <div className="questions-container">
                    {section.questions.map((question, questionIndex) => {
                      const isQuestionSubmitted =
                        submittedQuestions[question.id];
                      const hasResponse = responses[question.id] !== undefined;
                      const globalQuestionNumber = getQuestionNumber(
                        question.id
                      );

                      return (
                        <div key={question.id} className="question-item">
                          <div className="question-header">
                            <div className="question-number">
                              {globalQuestionNumber}.
                            </div>
                            <div
                              className={`question-status ${
                                isQuestionSubmitted
                                  ? "submitted"
                                  : hasResponse
                                  ? "answered"
                                  : "unanswered"
                              }`}
                            >
                              {isQuestionSubmitted
                                ? "Submitted"
                                : hasResponse
                                ? "Answered"
                                : "Not Answered"}
                            </div>
                          </div>

                          <div className="question-content">
                            <div className="question-text">
                              <RichContentRenderer
                                content={
                                  question.richContent || question.content
                                }
                                isNonEditable={true}
                              />
                              {question.marks && (
                                <span className="question-marks">
                                  [{question.marks} marks]
                                </span>
                              )}
                            </div>

                            {question.type === "with-options" &&
                              question.options && (
                                <div className="question-options">
                                  {question.options.map(
                                    (option, optionIndex) => {
                                      const isSelected =
                                        responses[question.id] === optionIndex;
                                      const isDisabled =
                                        isSubmitted || isQuestionSubmitted;

                                      return (
                                        <div
                                          key={option.id || optionIndex}
                                          className={`option ${
                                            isSelected ? "selected" : ""
                                          } ${isDisabled ? "disabled" : ""}`}
                                          onClick={() =>
                                            handleOptionSelect(
                                              question.id,
                                              optionIndex
                                            )
                                          }
                                        >
                                          <div className="option-selector">
                                            <input
                                              type="radio"
                                              name={`question_${question.id}`}
                                              checked={isSelected}
                                              onChange={() =>
                                                handleOptionSelect(
                                                  question.id,
                                                  optionIndex
                                                )
                                              }
                                              disabled={isDisabled}
                                              className="option-radio"
                                            />
                                            <span className="option-label">
                                              {String.fromCharCode(
                                                65 + optionIndex
                                              )}
                                            </span>
                                          </div>
                                          <div className="option-content">
                                            <RichContentRenderer
                                              content={
                                                option.richContent || {
                                                  type: "text",
                                                  content:
                                                    option.content || option,
                                                }
                                              }
                                              isNonEditable={true}
                                            />
                                          </div>
                                        </div>
                                      );
                                    }
                                  )}
                                </div>
                              )}

                            <div className="question-actions">
                              <button
                                onClick={() =>
                                  handleQuestionSubmit(question.id)
                                }
                                disabled={
                                  isSubmitted ||
                                  isQuestionSubmitted ||
                                  !hasResponse
                                }
                                className={`question-submit-btn ${
                                  isQuestionSubmitted
                                    ? "submitted"
                                    : !hasResponse
                                    ? "disabled"
                                    : ""
                                }`}
                              >
                                {isQuestionSubmitted ? (
                                  <>
                                    <CheckCircle size={16} />
                                    Submitted
                                  </>
                                ) : (
                                  "Submit Answer"
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Submit Button */}
      <div className="exam-footer">
        <div className="submit-container">
          <button
            onClick={handleSubmit}
            disabled={isSubmitted}
            className={`submit-btn ${isSubmitted ? "submitted" : ""}`}
          >
            {isSubmitted ? (
              <>
                <CheckCircle size={20} />
                Submitted
              </>
            ) : (
              "Submit Exam"
            )}
          </button>
        </div>
      </div>

      {/* Submit Confirmation Modal */}
      {showSubmitConfirmation && (
        <div className="modal-overlay">
          <div className="confirmation-modal">
            <h3>Confirm Submission</h3>
            <p>
              You have answered {getAnsweredQuestions()} out of{" "}
              {getTotalQuestions()} questions.
              <br />
              Are you sure you want to submit your exam?
            </p>
            <div className="modal-actions">
              <button
                onClick={() => setShowSubmitConfirmation(false)}
                className="cancel-btn"
              >
                Cancel
              </button>
              <button onClick={confirmSubmit} className="confirm-btn">
                Submit Exam
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .exam-page {
          min-height: 100vh;
          background: #f5f7fa;
          font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI",
            sans-serif;
        }

        .loading-container,
        .error-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 60vh;
          gap: 1rem;
        }

        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #e5e7eb;
          border-top: 4px solid #3b82f6;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        .error-icon {
          width: 48px;
          height: 48px;
          color: #ef4444;
        }

        .retry-btn {
          padding: 0.75rem 1.5rem;
          background: #3b82f6;
          color: white;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 500;
        }

        .retry-btn:hover {
          background: #2563eb;
        }

        .exam-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-bottom: none;
          padding: 1.5rem 2rem;
          position: sticky;
          top: 0;
          z-index: 100;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
        }

        .exam-header-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
          max-width: 1600px;
          margin: 0 auto;
        }

        .exam-info h1 {
          margin: 0 0 0.25rem 0;
          font-size: 1.75rem;
          font-weight: 700;
          color: white;
        }

        .exam-subject {
          margin: 0;
          color: rgba(255, 255, 255, 0.9);
          font-size: 1rem;
        }

        .exam-stats {
          display: flex;
          gap: 1.5rem;
          align-items: center;
        }

        .timer {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.25rem;
          background: rgba(255, 255, 255, 0.15);
          backdrop-filter: blur(10px);
          color: white;
          border-radius: 12px;
          font-weight: 600;
          font-size: 1.1rem;
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .timer-warning {
          background: rgba(255, 107, 107, 0.9);
          animation: pulse 2s infinite;
        }

        .progress-info {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: rgba(255, 255, 255, 0.9);
          font-weight: 500;
          padding: 0.75rem 1.25rem;
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          border-radius: 12px;
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .exam-content {
          padding: 2rem;
          max-width: 1600px;
          margin: 0 auto;
          min-height: calc(100vh - 200px);
        }

        .paper-container {
          background: white;
          border-radius: 16px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
          overflow: hidden;
        }

        .exam-page-content {
          padding: 3rem 4rem;
        }

        .exam-details-header {
          text-align: center;
          border-bottom: 3px solid #667eea;
          padding-bottom: 2rem;
          margin-bottom: 3rem;
          background: linear-gradient(135deg, #f8faff 0%, #f1f5ff 100%);
          padding: 2rem;
          border-radius: 12px;
          margin: -3rem -4rem 3rem -4rem;
        }

        .exam-header-main h1 {
          font-size: 2rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 1px;
          margin: 0 0 0.5rem 0;
          color: #1e293b;
          background: linear-gradient(135deg, #667eea, #764ba2);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .exam-subject-title {
          font-size: 1.4rem;
          font-weight: 600;
          margin: 0 0 1.5rem 0;
          color: #475569;
        }

        .exam-metadata {
          display: flex;
          justify-content: space-between;
          border-top: 2px solid #e2e8f0;
          border-bottom: 2px solid #e2e8f0;
          padding: 1rem 0;
          margin: 1.5rem 0;
          font-weight: 600;
          color: #1e293b;
          background: white;
          border-radius: 8px;
          padding: 1rem 2rem;
        }

        .exam-instructions {
          text-align: left;
          margin-top: 1.5rem;
          padding: 1.5rem;
          background: #f8fafc;
          border-radius: 12px;
          font-size: 0.95rem;
          line-height: 1.6;
          border-left: 4px solid #667eea;
        }

        .section {
          margin: 3rem 0;
          background: #fafbfc;
          border: 1px solid #e2e8f0;
          border-radius: 16px;
          padding: 2rem;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
        }

        .section-header h3 {
          font-size: 1.4rem;
          font-weight: 700;
          color: #1e293b;
          margin: 0 0 1.5rem 0;
          padding-bottom: 0.75rem;
          border-bottom: 3px solid #667eea;
          background: linear-gradient(135deg, #667eea, #764ba2);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .section-instructions {
          background: #ecfdf5;
          border: 1px solid #d1fae5;
          border-radius: 12px;
          padding: 1.25rem;
          margin-bottom: 2rem;
          border-left: 4px solid #10b981;
        }

        .instructions-label {
          font-weight: 700;
          color: #065f46;
          margin-bottom: 0.5rem;
          font-size: 0.9rem;
        }

        .instructions-content {
          color: #065f46;
          line-height: 1.6;
        }

        .questions-container {
          display: flex;
          flex-direction: column;
          gap: 2rem;
        }

        .question-item {
          background: white;
          border: 2px solid #e2e8f0;
          border-radius: 16px;
          padding: 2rem;
          transition: all 0.3s ease;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
        }

        .question-item:hover {
          border-color: #cbd5e1;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
          transform: translateY(-2px);
        }

        .question-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
          padding-bottom: 1rem;
          border-bottom: 2px solid #f1f5f9;
        }

        .question-number {
          font-size: 1.25rem;
          font-weight: 800;
          color: #667eea;
          background: linear-gradient(135deg, #667eea, #764ba2);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .question-status {
          padding: 0.5rem 1rem;
          border-radius: 20px;
          font-size: 0.8rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .question-status.submitted {
          background: #dcfce7;
          color: #166534;
          border: 1px solid #bbf7d0;
        }

        .question-status.answered {
          background: #dbeafe;
          color: #1e40af;
          border: 1px solid #bfdbfe;
        }

        .question-status.unanswered {
          background: #fef3c7;
          color: #d97706;
          border: 1px solid #fed7aa;
        }

        .question-content {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .question-text {
          font-size: 1.05rem;
          line-height: 1.7;
          color: #1e293b;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
        }

        .question-marks {
          font-size: 0.875rem;
          color: #64748b;
          font-weight: 600;
          margin-left: 1rem;
          white-space: nowrap;
          background: #f1f5f9;
          padding: 0.25rem 0.75rem;
          border-radius: 16px;
        }

        .question-options {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1rem;
        }

        .option {
          display: flex;
          align-items: flex-start;
          padding: 1rem 1.25rem;
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.2s ease;
          background: #fafbfc;
          gap: 1rem;
        }

        .option:hover:not(.disabled) {
          border-color: #667eea;
          background: #f8faff;
          transform: translateX(4px);
        }

        .option.selected {
          border-color: #667eea;
          background: linear-gradient(135deg, #f8faff 0%, #f1f5ff 100%);
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.15);
        }

        .option.disabled {
          cursor: not-allowed;
          opacity: 0.6;
          background: #f8fafc;
        }

        .option-selector {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          min-width: 80px;
        }

        .option-radio {
          width: 20px;
          height: 20px;
          accent-color: #667eea;
          cursor: pointer;
        }

        .option-label {
          font-weight: 700;
          color: #667eea;
          font-size: 0.9rem;
          background: white;
          width: 28px;
          height: 28px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 2px solid #667eea;
        }

        .option.selected .option-label {
          background: #667eea;
          color: white;
        }

        .option-content {
          flex: 1;
          line-height: 1.6;
          color: #374151;
        }

        .question-actions {
          display: flex;
          justify-content: flex-end;
          padding-top: 1rem;
          border-top: 1px solid #f1f5f9;
        }

        .question-submit-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          background: linear-gradient(135deg, #10b981, #059669);
          color: white;
          border: none;
          border-radius: 10px;
          font-size: 0.9rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.25);
        }

        .question-submit-btn:hover:not(:disabled) {
          background: linear-gradient(135deg, #059669, #047857);
          transform: translateY(-1px);
          box-shadow: 0 6px 16px rgba(16, 185, 129, 0.3);
        }

        .question-submit-btn.submitted {
          background: linear-gradient(135deg, #6b7280, #4b5563);
          cursor: not-allowed;
          box-shadow: none;
        }

        .question-submit-btn.disabled {
          background: #d1d5db;
          color: #9ca3af;
          cursor: not-allowed;
          box-shadow: none;
        }

        .question-submit-btn:disabled {
          cursor: not-allowed;
          opacity: 0.7;
        }

        .exam-footer {
          background: linear-gradient(135deg, #f8faff 0%, #f1f5ff 100%);
          border-top: 2px solid #e2e8f0;
          padding: 2rem;
          position: sticky;
          bottom: 0;
          z-index: 100;
          backdrop-filter: blur(10px);
        }

        .submit-container {
          max-width: 1600px;
          margin: 0 auto;
          text-align: center;
        }

        .submit-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.75rem;
          padding: 1.25rem 3rem;
          background: linear-gradient(135deg, #dc2626, #b91c1c);
          color: white;
          border: none;
          border-radius: 16px;
          font-size: 1.1rem;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 8px 24px rgba(220, 38, 38, 0.3);
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .submit-btn:hover:not(:disabled) {
          background: linear-gradient(135deg, #b91c1c, #991b1b);
          transform: translateY(-2px);
          box-shadow: 0 12px 32px rgba(220, 38, 38, 0.4);
        }

        .submit-btn.submitted {
          background: linear-gradient(135deg, #6b7280, #4b5563);
          cursor: not-allowed;
          box-shadow: none;
        }

        .submit-btn:disabled {
          cursor: not-allowed;
          opacity: 0.7;
        }

        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.6);
          backdrop-filter: blur(8px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .confirmation-modal {
          background: white;
          padding: 2.5rem;
          border-radius: 20px;
          max-width: 500px;
          width: 90%;
          text-align: center;
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .confirmation-modal h3 {
          margin: 0 0 1.5rem 0;
          color: #1f2937;
          font-size: 1.4rem;
          font-weight: 700;
        }

        .confirmation-modal p {
          margin: 0 0 2rem 0;
          color: #4b5563;
          line-height: 1.6;
          font-size: 1rem;
        }

        .modal-actions {
          display: flex;
          gap: 1rem;
          justify-content: center;
        }

        .cancel-btn,
        .confirm-btn {
          padding: 0.875rem 1.75rem;
          border: none;
          border-radius: 12px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 0.95rem;
        }

        .cancel-btn {
          background: #f3f4f6;
          color: #374151;
          border: 2px solid #e5e7eb;
        }

        .cancel-btn:hover {
          background: #e5e7eb;
          border-color: #d1d5db;
        }

        .confirm-btn {
          background: linear-gradient(135deg, #dc2626, #b91c1c);
          color: white;
          box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3);
        }

        .confirm-btn:hover {
          background: linear-gradient(135deg, #b91c1c, #991b1b);
          transform: translateY(-1px);
          box-shadow: 0 6px 16px rgba(220, 38, 38, 0.4);
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        @keyframes pulse {
          0%,
          100% {
            opacity: 1;
          }
          50% {
            opacity: 0.7;
          }
        }

        /* Responsive Design */
        @media (max-width: 1400px) {
          .exam-content {
            max-width: 1400px;
            padding: 1.5rem;
          }

          .exam-page-content {
            padding: 2.5rem 3rem;
          }
        }

        @media (max-width: 1200px) {
          .exam-content {
            max-width: 1200px;
          }

          .exam-page-content {
            padding: 2rem 2.5rem;
          }

          .question-item {
            padding: 1.5rem;
          }
        }

        @media (max-width: 768px) {
          .exam-header {
            padding: 1rem;
          }

          .exam-header-content {
            flex-direction: column;
            gap: 1rem;
          }

          .exam-stats {
            flex-direction: column;
            gap: 1rem;
            width: 100%;
          }

          .timer,
          .progress-info {
            width: 100%;
            justify-content: center;
          }

          .exam-content {
            padding: 1rem;
          }

          .exam-page-content {
            padding: 1.5rem;
          }

          .exam-details-header {
            margin: -1.5rem -1.5rem 2rem -1.5rem;
            padding: 1.5rem;
          }

          .exam-metadata {
            flex-direction: column;
            gap: 0.5rem;
            text-align: center;
          }

          .section {
            padding: 1.5rem;
          }

          .question-item {
            padding: 1.25rem;
          }

          .question-header {
            flex-direction: column;
            gap: 0.75rem;
            align-items: flex-start;
          }

          .question-text {
            flex-direction: column;
            gap: 0.5rem;
          }

          .question-marks {
            margin-left: 0;
            align-self: flex-start;
          }

          .option {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.75rem;
          }

          .option-selector {
            width: 100%;
          }

          .modal-actions {
            flex-direction: column;
          }

          .confirmation-modal {
            padding: 2rem;
          }

          .submit-btn {
            padding: 1rem 2rem;
            font-size: 1rem;
          }
        }

        /* Print Styles */
        @media print {
          .exam-header,
          .exam-footer {
            display: none;
          }

          .exam-page {
            background: white;
          }

          .paper-container {
            box-shadow: none;
            border-radius: 0;
          }

          .question-item {
            break-inside: avoid;
            background: white;
            border: 1px solid #ccc;
            box-shadow: none;
          }

          .section {
            break-inside: avoid;
            background: white;
            box-shadow: none;
          }

          .question-actions {
            display: none;
          }
        }
      `}</style>
    </div>
  );
};

export default ExamPage;